import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Ruler,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { UoMMaster, CreateUoMMasterData } from "../../../services/uomMasterService";
import Select from "../../../shared/components/form/Select";
import Button from "../../../shared/components/ui/button/Button";
import { UOM_CATEGORIES } from "../constants/uomConstants";

const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

interface Props {
  onAdd: (uomMasterData: CreateUoMMasterData) => Promise<void>;
  onClose?: () => void;
  editingUoMMaster?: UoMMaster | null;
  showSuccessAndClose?: boolean;
}

const schema = yup.object({
  name: yup.string().required("Name is required").trim().max(200, "Name must be less than 200 characters"),
  symbol: yup.string().required("Symbol is required").trim().max(50, "Symbol must be less than 50 characters"),
  category: yup.string().required("Category is required"),
  description: yup.string().optional().trim().max(500, "Description must be less than 500 characters"),
});

interface FormValues {
  name: string;
  symbol: string;
  category: string;
  description?: string;
}

export default function UoMMasterForm({ onAdd, onClose, editingUoMMaster, showSuccessAndClose = true }: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const isEditMode = Boolean(editingUoMMaster);

  // Set default values based on edit mode
  const getDefaultValues = (): Partial<FormValues> => {
    if (editingUoMMaster) {
      return {
        name: editingUoMMaster.name || '',
        symbol: editingUoMMaster.symbol || '',
        category: editingUoMMaster.category || '',
        description: editingUoMMaster.description || '',
      };
    }
    return {
      name: '',
      symbol: '',
      category: '',
      description: '',
    };
  };

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues(),
    shouldUnregister: false
  });

  const watchedName = watch("name");
  const watchedSymbol = watch("symbol");
  const watchedCategory = watch("category");

  const categoryOptions = UOM_CATEGORIES;

  const onSubmit = async (data: FormValues) => {
    setApiError(null);
    setShowSuccess(false);

    try {
      const uomMasterData: CreateUoMMasterData = {
        name: data.name.trim(),
        symbol: data.symbol.trim(),
        category: data.category,
        description: data.description?.trim() || undefined,
      };

      // Call the parent component's handler
      await onAdd(uomMasterData);

      // Only show success message and auto-close if parent wants us to
      if (showSuccessAndClose) {
        // Show success message
        setShowSuccess(true);
        toast.success(
          isEditMode
            ? "UOM updated successfully!"
            : "UOM created successfully!"
        );

        // Close form after a short delay to show success message
        setTimeout(() => {
          onClose?.();
          reset();
        }, 1500);
      } else {
        // Parent is handling success/close, just reset the form
        reset();
      }
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      let message = error.response?.data?.message || error.message || "Failed to save UOM. Please try again.";

      // Handle specific validation errors
      if (message.includes("duplicate key error") || message.includes("already exists")) {
        // Use the backend message if it's already descriptive, otherwise provide a generic one
        if (!message.includes("already exists")) {
          message = "A UOM with the same name, symbol, and category already exists. Please use a different combination.";
        }
      } else if (message.includes("UOM validation failed")) {
        message = "Please check all required fields and try again.";
      }

      setApiError(message);
      console.error("UOM Save Error:", error);
    }
  };

  const formTitle = isEditMode ? "Edit UOM" : "Add New UOM";

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col"
    >
      {/* Success Message */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.successLight,
              borderColor: `${BRAND_COLORS.success}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.success + '20',
                borderColor: BRAND_COLORS.success + '40'
              }}
            >
              <CheckCircle2 className="w-5 h-5" style={{ color: BRAND_COLORS.success }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.success }}>
                UOM {isEditMode ? 'updated' : 'created'} successfully!
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.success }}>
                {isEditMode ? 'UOM information has been updated.' : 'UOM has been added to the system.'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Alert */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: BRAND_COLORS.errorLight,
              borderColor: `${BRAND_COLORS.error}40`
            }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.error + '20',
                borderColor: BRAND_COLORS.error + '40'
              }}
            >
              <AlertTriangle className="w-5 h-5" style={{ color: BRAND_COLORS.error }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.error }}>Error</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.error }}>{apiError}</p>
            </div>
            <button
              onClick={() => setApiError(null)}
              className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
              style={{ color: BRAND_COLORS.error }}
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Header */}
      <div className="flex items-center gap-3 pb-5 border-b border-gray-200 mb-6">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center border"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
            borderColor: BRAND_COLORS.primary
          }}
        >
          <Ruler className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">{formTitle}</h2>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {isEditMode
              ? "Update UOM information"
              : "Create a new UOM with unit of measure details"
            }
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Form Body - Scrollable */}
      <div className="flex-1 pr-2 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
              <h3 className="text-base font-semibold text-gray-900">UOM Information</h3>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-900">
                UOM name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  {...register("name")}
                  className={`h-10 w-full rounded-lg border bg-white pl-3 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedName && !errors.name
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedName && !errors.name ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.name ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter display name"
                />
                {watchedName && !errors.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.name && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-900">
                UOM Category <span className="text-red-500">*</span>
              </label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select
                    options={categoryOptions}
                    value={categoryOptions.find((opt) => opt.value === field.value)}
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select category"
                    error={errors.category?.message}
                  />
                )}
              />
              {errors.category && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.category.message}
                </p>
              )}
            </div>

            {/* Symbol */}
            <div className="space-y-2">
              <label htmlFor="symbol" className="block text-sm font-medium text-gray-900">
                UOM Symbol <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="symbol"
                  type="text"
                  {...register("symbol")}
                  className={`h-10 w-full rounded-lg border bg-white pl-3 pr-10 py-3 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.symbol
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedSymbol && !errors.symbol
                      ? `focus:ring-opacity-20`
                      : "border-gray-300 hover:border-gray-400"
                    }`}
                  style={watchedSymbol && !errors.symbol ? {
                    borderColor: BRAND_COLORS.primary,
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : !errors.symbol ? {
                    '--tw-ring-color': BRAND_COLORS.primary + '40'
                  } as React.CSSProperties : {}}
                  placeholder="Enter symbol (e.g., kg, m, pcs)"
                />
                {watchedSymbol && !errors.symbol && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: BRAND_COLORS.primary }} />
                )}
                {errors.symbol && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                )}
              </div>
              {errors.symbol && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.symbol.message}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <textarea
                id="description"
                {...register("description")}
                rows={3}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.description
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 hover:border-gray-400"
                  }`}
                style={!errors.description ? {
                  '--tw-ring-color': BRAND_COLORS.primary + '40'
                } as React.CSSProperties : {}}
                placeholder="Enter optional description"
              />
              {errors.description && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {errors.description.message}
                </p>
              )}
            </div>

          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !watchedName || !watchedSymbol || !watchedCategory}
              className="px-8 min-w-[140px]"
              style={{ backgroundColor: BRAND_COLORS.primary }}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </div>
              ) : (
                <span>{isEditMode ? 'Update UOM' : 'Add UOM'}</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
